"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  createWikiPage,
  fetchWikiTree,
} from "@/api/wiki";
import { viewRefKey, type ViewRef } from "@/api/views";
import { FileBrowserShell } from "@/components/views/file-browser-shell";
import type { BrowserLabels, BrowserTreeResponse } from "@/components/views/file-browser-types";
import { FileTreeSidebar } from "@/components/views/file-tree-sidebar";
import { MillerColumns } from "@/components/views/miller-columns";
import { useFileBrowserNavigation } from "@/components/views/use-file-browser-navigation";
import { WikiPageView } from "@/components/views/wiki-page-view";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import type { AccessTokenGetter } from "@/lib/utils";

export interface WikiViewProps {
  viewRef: ViewRef;
  initialPath?: string | null;
  getToken: AccessTokenGetter;
  /** Panel = Miller columns; page = persistent tree sidebar and page canvas. */
  variant: "panel" | "page";
  className?: string;
}

function pageTitleFromName(name: string): string {
  return name
    .replace(/\.md$/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function wikiTreeResponse(response: Awaited<ReturnType<typeof fetchWikiTree>>): BrowserTreeResponse {
  return {
    path: response.path,
    directories: response.directories.map((entry) => ({
      name: entry.name,
      path: entry.wiki_path,
      summary: entry.summary,
    })),
    files: response.files.map((entry) => ({
      name: entry.name,
      path: entry.wiki_path,
      summary: entry.summary,
    })),
    canWrite: response.can_write,
  };
}

interface CreatePageDialogProps {
  directoryPath: string | null;
  onOpenChange: (open: boolean) => void;
  onCreate: (name: string) => Promise<string | null>;
}

function CreatePageDialog({ directoryPath, onOpenChange, onCreate }: CreatePageDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = directoryPath !== null;
  const directoryLabel = directoryPath ? directoryPath : t("wiki.root");

  useEffect(() => {
    if (!open) return;
    setName("");
    setError(null);
    setCreating(false);
  }, [open]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) {
      setError(t("wiki.pageNameRequired"));
      return;
    }
    setCreating(true);
    setError(null);
    const failure = await onCreate(name);
    setCreating(false);
    if (failure) setError(failure);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton>
        <form onSubmit={(event) => void submit(event)}>
          <DialogHeader>
            <DialogTitle>{t("wiki.newPage")}</DialogTitle>
            <DialogDescription>{t("wiki.newPageDescription", { directory: directoryLabel })}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            <label className="text-sm font-medium" htmlFor="wiki-page-name">
              {t("wiki.pageName")}
            </label>
            <Input
              id="wiki-page-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
              disabled={creating}
              data-testid="wiki-new-page-name"
            />
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          </div>
          <DialogFooter className="mt-5">
            <Button type="button" variant="outline" disabled={creating} onClick={() => onOpenChange(false)}>
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" disabled={creating} data-testid="wiki-create-page">
              {creating && <Spinner className="size-3.5" />}
              {t(creating ? "wiki.creatingPage" : "common:actions.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Wiki-specific extensions on top of the shared file-browser shell:
 * page creation, writable Markdown editing, backlinks, and wikilinks.
 */
export function WikiView({
  viewRef,
  initialPath,
  getToken,
  variant,
  className,
}: WikiViewProps) {
  const { t } = useTranslation();
  const viewKey = viewRefKey(viewRef);
  const [columnsState, setColumnsState] = useState<string[]>([]);
  const [createDirectory, setCreateDirectory] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Wiki paths are canonically extensionless, while the shared browser hook
  // uses a Markdown suffix to distinguish a page from a directory.
  const initialPage = initialPath
    ? `${initialPath.replace(/\.md$/i, "")}.md`
    : null;
  const navigation = useFileBrowserNavigation(initialPage);

  const labels = useMemo<BrowserLabels>(() => ({
    root: t("wiki.root"),
    loading: t("wiki.loading"),
    unableToLoad: t("wiki.unableToLoad"),
    openDirectory: (name) => t("wiki.openDirectory", { name }),
    viewFile: (name) => t("wiki.openPage", { name }),
    downloadFile: (name) => t("wiki.openPage", { name }),
    emptyDirectory: t("wiki.emptyDirectory"),
  }), [t]);

  const fetchTree = useCallback(
    async (path: string) => wikiTreeResponse(await fetchWikiTree(viewRef, path, getToken)),
    [getToken, viewKey],
  );

  const createPage = useCallback(
    async (name: string): Promise<string | null> => {
      const cleanName = name.trim().replace(/\.md$/i, "");
      if (!cleanName || cleanName.includes("/") || cleanName.includes("\\")) {
        return t("wiki.pageNameInvalid");
      }
      const path = createDirectory ? `${createDirectory}/${cleanName}` : cleanName;
      try {
        const page = await createWikiPage(
          viewRef,
          {
            path,
            content: `# ${pageTitleFromName(cleanName)}\n\n`,
          },
          getToken,
        );
        setCreateDirectory(null);
        setRefreshKey((value) => value + 1);
        navigation.openPage(page.path);
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : t("wiki.unableToCreate");
      }
    },
    [createDirectory, getToken, navigation, t, viewRef],
  );

  const pageView = navigation.activePage ? (
    <WikiPageView
      viewRef={viewRef}
      wikiPath={navigation.activePage}
      getToken={getToken}
      onBack={navigation.goBack}
      onClose={navigation.closePage}
      onNavigatePage={navigation.openPage}
    />
  ) : null;

  return (
    <>
      <FileBrowserShell
        variant={variant}
        mode={navigation.mode}
        testIdPrefix="wiki"
        className={className}
        columns={(
          <MillerColumns
            fetchTree={fetchTree}
            labels={labels}
            selectedDirectories={columnsState}
            onDirectoriesChange={setColumnsState}
            selectedFilePath={navigation.lastSelectedFile}
            onSelectFile={(file) => navigation.openPage(file.path)}
            onCreatePage={setCreateDirectory}
            refreshKey={refreshKey}
            displayFileName={(file) => file.name.replace(/\.md$/i, "")}
          />
        )}
        sidebar={(
          <FileTreeSidebar
            fetchTree={fetchTree}
            labels={labels}
            title={t("wiki.title")}
            sidebarLabel={t("wiki.sidebar")}
            selectedFilePath={navigation.lastSelectedFile}
            onSelectFile={(file) => navigation.openPage(file.path)}
            onCreatePage={setCreateDirectory}
            refreshKey={refreshKey}
            displayFileName={(file) => file.name.replace(/\.md$/i, "")}
          />
        )}
        pageView={pageView}
        placeholder={(
          <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground" data-testid="wiki-page-placeholder">
            {t("wiki.selectPage")}
          </div>
        )}
      />
      <CreatePageDialog
        directoryPath={createDirectory}
        onOpenChange={(open) => { if (!open) setCreateDirectory(null); }}
        onCreate={createPage}
      />
    </>
  );
}
